import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Text, Alert, Loader, Group, ActionIcon, TextInput, Tooltip, PillsInput, Pill } from '@mantine/core';
import { IconSearch, IconAlertCircle, IconX } from '@tabler/icons-react';
import { useFieldOptions, useFieldOptionsForEntityType, useSchemaLoading, useSchemaError } from '../../store/schemaHooks';
import { EntityBrowserDialog } from '../dialogs';
import EntitlementsService from '../../services/EntitlementsService';
import { buildFormAttributesFromTier2Attributes, buildFormAttributesFromTier2AttributeBindings, isEntitySetFacade, type FormAttributeOption } from '../../types/EntityEntitlements';
import { createEntityRef, findEntityRefByKey } from '../../types/entity/EntityRef';
import { debugLog } from '../../utils/debugLog';

interface AttributeSelectorProps {
    value: string | string[] | null;
    onChange: (value: string | string[] | null) => void;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    allowClear?: boolean;
    multiValued?: boolean;
    entityType?: string;
    lockAfterSelection?: boolean;
    filterReferenceOnly?: boolean;
}

export const AttributeSelector: React.FC<AttributeSelectorProps> = ({
    value,
    onChange,
    label = "Attribute",
    required = false,
    disabled = false,
    error = false,
    helperText,
    allowClear = true,
    multiValued = false,
    entityType,
    lockAfterSelection = true,
    filterReferenceOnly = false
}) => {
    const [browserOpen, setBrowserOpen] = useState(false);
    const [tier2OptionsByEntityType, setTier2OptionsByEntityType] = useState<Record<string, FormAttributeOption[]>>({});
    const [tier2Loading, setTier2Loading] = useState(false);
    const fetchRequestedRef = useRef<Record<string, boolean>>({});

    const allFieldOptions = useFieldOptions();
    const entityTypeFieldOptions = useFieldOptionsForEntityType(entityType || '');
    const schemaLoading = useSchemaLoading();
    const schemaError = useSchemaError();

    const normalizedEntityType = (entityType || '').trim().toLowerCase();
    useEffect(() => {
        if (!normalizedEntityType) return;
        if (entityTypeFieldOptions.length > 0) return;
        if (tier2OptionsByEntityType[normalizedEntityType] !== undefined) return;
        if (fetchRequestedRef.current[normalizedEntityType]) return;

        fetchRequestedRef.current[normalizedEntityType] = true;
        setTier2Loading(true);
        EntitlementsService.fetchEntityEntitlementForSchemaEntity(normalizedEntityType)
            .then((tier2) => {
                const options = isEntitySetFacade(tier2)
                    ? buildFormAttributesFromTier2Attributes(tier2?.Attributes)
                    : (buildFormAttributesFromTier2AttributeBindings(tier2?.AttributeBindings) ?? buildFormAttributesFromTier2Attributes(tier2?.Attributes));
                setTier2OptionsByEntityType((prev) => ({ ...prev, [normalizedEntityType]: options }));
            })
            .catch((err) => {
                console.error(`Selector entitlement (tier2 attribute options) failed for entityType "${normalizedEntityType}":`, err);
                setTier2OptionsByEntityType((prev) => ({ ...prev, [normalizedEntityType]: [] }));
            })
            .finally(() => {
                setTier2Loading(false);
                delete fetchRequestedRef.current[normalizedEntityType];
            });
    }, [normalizedEntityType, entityTypeFieldOptions.length]);

    const fieldOptionsFromRedux = entityType ? entityTypeFieldOptions : allFieldOptions;
    const tier2Options = normalizedEntityType ? tier2OptionsByEntityType[normalizedEntityType] : undefined;
    const fieldOptions = useMemo(() => {
        if (fieldOptionsFromRedux.length > 0) return fieldOptionsFromRedux;
        if (tier2Options !== undefined) return tier2Options;
        return [];
    }, [fieldOptionsFromRedux, tier2Options]);

    const loading = schemaLoading || (!!normalizedEntityType && entityTypeFieldOptions.length === 0 && tier2Loading);

    const attributes = useMemo(() => {
        if (filterReferenceOnly) {
            return fieldOptions.filter((option: any) => {
                if (option.isReference === true) return true;
                const attrName = (option.name || option.id || '').toLowerCase();
                if (attrName === 'id' || attrName === '$.id') return true;
                return false;
            });
        }
        return fieldOptions;
    }, [fieldOptions, filterReferenceOnly]);

    // Unified: Accepts both [{ Id }] and string[] for backward compatibility
    const handleBrowserConfirm = (input: Array<{ Id: string; DisplayName: string; EntityType: string }> | string[]) => {
        let processedIds: string[];
        if (Array.isArray(input) && input.length > 0 && typeof input[0] === 'object' && 'Id' in input[0]) {
            processedIds = (input as Array<{ Id: string }>).map((e) => e.Id);
        } else {
            processedIds = input as string[];
        }

        if (multiValued) {
            const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
            const newValues = [...currentValues, ...processedIds.filter(id => !currentValues.includes(id))];
            const valueToPass = newValues.length > 0 ? newValues : null;
            debugLog('[AttributeSelector] popup selected value:', valueToPass, { multiValued, processedIds });
            onChange(valueToPass);
        } else {
            const valueToPass = processedIds.length > 0 ? processedIds[0] : null;
            debugLog('[AttributeSelector] popup selected value:', valueToPass, { multiValued, processedIds });
            onChange(valueToPass);
        }
        setBrowserOpen(false);
    };

    const attributeRefs = useMemo(() => attributes.map((attr: any) => createEntityRef(attr)), [attributes]);

    const isBrowserDisabled = disabled || !entityType || (lockAfterSelection && !!value);

    const canClear = allowClear && !disabled;
    const canRemoveItems = !disabled && !(lockAfterSelection && !!value);

    const selectedIds = useMemo((): string[] => {
        if (!value) return [];
        if (Array.isArray(value)) return value.map((v) => String(v ?? '').trim()).filter(Boolean);
        return [String(value).trim()].filter(Boolean);
    }, [value]);

    const getDisplayLabel = (id: string): string => {
        const ref = findEntityRefByKey(attributeRefs, id);
        return ref ? ref.displayName : id;
    };

    // Display text for single-valued mode
    const displayValue = useMemo(() => {
        if (multiValued) return '';
        const id = selectedIds[0];
        return id ? getDisplayLabel(id) : '';
    }, [multiValued, selectedIds, attributeRefs]);

    const removeMultiId = (id: string) => {
        if (!canRemoveItems) return;
        const next = selectedIds.filter((x) => x !== id);
        onChange(next.length > 0 ? next : null);
    };

    return (
        <Box>
            {multiValued ? (
                <PillsInput
                    label={required ? `${label} *` : label}
                    disabled={disabled}
                    error={error}
                    rightSection={
                        <Group gap={4} wrap="nowrap" pr="xs">
                            <Tooltip label="Select attributes">
                                <ActionIcon
                                    variant="subtle"
                                    size="sm"
                                    onClick={() => setBrowserOpen(true)}
                                    disabled={isBrowserDisabled}
                                    aria-label="Select attributes"
                                >
                                    {loading ? <Loader size={16} /> : <IconSearch size={16} />}
                                </ActionIcon>
                            </Tooltip>
                            {selectedIds.length > 0 && canClear && (
                                <Tooltip label="Clear">
                                    <ActionIcon
                                        variant="subtle"
                                        size="sm"
                                        color="red"
                                        onClick={() => onChange(null)}
                                        disabled={disabled}
                                        aria-label="Clear"
                                    >
                                        <IconX size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </Group>
                    }
                    rightSectionWidth={selectedIds.length > 0 && canClear ? 72 : 40}
                >
                    <Pill.Group>
                        {selectedIds.length === 0 && (
                            <PillsInput.Field
                                readOnly
                                placeholder={!entityType ? 'Select entity type first' : 'Select attributes...'}
                            />
                        )}
                        {selectedIds.map((id) => (
                            <Pill
                                key={id}
                                withRemoveButton={canRemoveItems}
                                onRemove={() => removeMultiId(id)}
                            >
                                {getDisplayLabel(id)}
                            </Pill>
                        ))}
                        {/* Keep a field so pills render in an input-like container (no dropdown). */}
                        <PillsInput.Field readOnly />
                    </Pill.Group>
                </PillsInput>
            ) : (
                <TextInput
                    label={required ? `${label} *` : label}
                    value={displayValue}
                    placeholder={!entityType ? 'Select entity type first' : 'Select an attribute...'}
                    readOnly
                    disabled={disabled}
                    error={error}
                    rightSection={
                        <Group gap={4} wrap="nowrap" pr="xs">
                            <Tooltip label="Select attribute">
                                <ActionIcon
                                    variant="subtle"
                                    size="sm"
                                    onClick={() => setBrowserOpen(true)}
                                    disabled={isBrowserDisabled}
                                    aria-label="Select attribute"
                                >
                                    {loading ? <Loader size={16} /> : <IconSearch size={16} />}
                                </ActionIcon>
                            </Tooltip>
                            {selectedIds.length > 0 && canClear && (
                                <Tooltip label="Clear">
                                    <ActionIcon
                                        variant="subtle"
                                        size="sm"
                                        color="red"
                                        onClick={() => onChange(null)}
                                        disabled={disabled}
                                        aria-label="Clear"
                                    >
                                        <IconX size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </Group>
                    }
                    rightSectionWidth={selectedIds.length > 0 && canClear ? 72 : 40}
                />
            )}
            {helperText && (
                <Text size="xs" c="dimmed" display="block" mt={4}>
                    {helperText}
                </Text>
            )}
            {!entityType && (
                <Text size="xs" c="dimmed" display="block" mt={4}>
                    Select an entity type first to choose attributes
                </Text>
            )}
            {lockAfterSelection && value && (
                <Text size="xs" c="dimmed" display="block" mt={4}>
                    Attribute selection is locked after making a choice
                </Text>
            )}
            {schemaError && (
                <Alert color="red" icon={<IconAlertCircle size={16} />} mt="xs">
                    {schemaError}
                </Alert>
            )}
            <EntityBrowserDialog
                open={browserOpen}
                onClose={() => setBrowserOpen(false)}
                onConfirm={handleBrowserConfirm}
                entityType="SchemaAttribute"
                title={`Select ${multiValued ? 'Attributes' : 'Attribute'}`}
                callerName="AttributeSelector"
                preloadedEntities={attributes.map((attr: any) => {
                    const attrId = attr.id || attr.name || '';
                    const displayName = attr.label || attr.name || attrId;
                    return {
                        Id: attrId,
                        DisplayName: displayName,
                        Name: displayName,
                        Type: 'SchemaAttribute'
                    };
                })}
                singleSelection={!multiValued}
            />
        </Box>
    );
};
